import type { PlayerColor } from '@/shared/board/types'
import { levelBrain } from '@/shared/bot/net/levels'
import type { Model } from '@/shared/bot/net/model'
import {
  type BonusField,
  beginFieldTurn,
  drawPrize,
  isExpiring,
  shieldedRefs,
  shieldToken,
  startField,
  takeBonus,
} from '@/shared/engine/bonus'
import { randomRoller, scriptedRoller } from '@/shared/engine/dice'
import { combos, comboView } from '@/shared/engine/resolver'
import {
  applyMove,
  type GameState,
  hasWon,
  restoreState,
  startState,
  withShields,
} from '@/shared/engine/state'
import {
  allowedFor,
  beginTurn,
  playTurn,
  restoreTurn,
  rollTurn,
  settleTurn,
  type Turn,
  turnView,
  withExtraRoll,
} from '@/shared/engine/turn'
import { forgetGame, markOver, rememberSecret } from '@/shared/game/storage'
import type {
  BotLevel,
  FeedEvent,
  GameStatus,
  GameView,
  Move,
  MoveStep,
  TokenPosition,
  Variant,
} from '@/shared/game/types'
import { kv } from '@/shared/platform/kv'

import {
  beatAt,
  expectedStep,
  narrowCombos,
  narrowMoves,
  pointersOf,
  sayOf,
  TUTORIAL,
  TUTORIAL_PLAYERS,
  type TutorialProgress,
  tutorialTokens,
} from './tutorial'

/**
 * Партия на одном устройстве.
 *
 * Сервера у неё нет: правила считает движок в браузере, ходы делаются по
 * очереди на том же экране. Наружу выдаётся ровно тот же GameView, что
 * присылает сервер, поэтому экран партии об этой разнице почти не знает —
 * ему всё равно, кто посчитал разрешённые ходы.
 *
 * Живёт в хранилище устройства (фасад `kv`, platform/kv): телефон гасит
 * вкладку, когда его убирают в карман, и партия не должна от этого
 * пропадать.
 */

const PREFIX = 'ludo.local.'

/** Столько событий держим в ленте — как FEED_LIMIT у сервера. */
const FEED_LIMIT = 30

/**
 * Столько партий за одним устройством храним.
 *
 * Удалять их некому: сервер о них не знает, а сами они не заканчиваются —
 * доигранная партия остаётся лежать. Место в хранилище не резиновое,
 * поэтому лишние вытесняются, начиная с самой старой.
 */
const KEEP = 5

/**
 * Игроки подписаны цветами.
 *
 * Спрашивать имена перед началом — лишний экран: за одним телефоном и так
 * видно, кто есть кто, а фишки на доске всё равно различаются цветом.
 */
const NAMES: Record<PlayerColor, string> = {
  red: 'Красный',
  green: 'Зелёный',
  yellow: 'Жёлтый',
  blue: 'Синий',
}

/** Порядок цветов по кругу — App\Domain\Ludo\PlayerColor. */
const ORDER: PlayerColor[] = ['red', 'green', 'yellow', 'blue']

/**
 * Имена компьютерных соперников — те же, что и у сервера (config/ludo.php).
 * Человеческое имя за столом читается как соперник, а «Компьютер 2» — как
 * настройка.
 */
const BOT_NAMES = ['Аня', 'Борис', 'Вера', 'Даша']

type Stored = {
  id: string
  /** Когда в партию последний раз ходили — по этому и вытесняются старые. */
  at: number
  players: PlayerColor[]
  /** Уровень компьютера по местам. null — за этим цветом играет человек. */
  bots: (BotLevel | null)[]
  tokens: Record<string, TokenPosition[]>
  turn: Turn
  status: GameStatus
  winner: PlayerColor | null
  feed: FeedEvent[]
  seq: number
  /**
   * Режим с бонусами (docs/BONUSES.md). Необязательны: партии, сохранённые
   * до появления режима, — классика.
   */
  variant?: Variant
  bonuses?: BonusField | null
  /** Обучение (tutorial.ts): где партия в сценарии. Нет поля — обычная партия. */
  tutorial?: TutorialProgress
}

function key(id: string): string {
  return `${PREFIX}${id}`
}

function read(id: string): Stored | null {
  try {
    const raw = kv.get(key(id))

    if (raw === null) return null

    const game = JSON.parse(raw) as Stored

    // Ход приводится к нынешней форме: партия переживает перезапуск
    // браузера, а отметки «фишка ходила» форму меняли (см. restoreTurn).
    return { ...game, turn: restoreTurn(game.turn) }
  } catch {
    return null
  }
}

function write(game: Stored): void {
  game.at = Date.now()
  kv.set(key(game.id), JSON.stringify(game))
}

/** Партия на этом устройстве, а не на сервере? */
export function isLocalGame(id: string): boolean {
  return read(id) !== null
}

export function dropLocalGame(id: string): void {
  kv.remove(key(id))

  forgetGame(id)
}

/** Все партии на этом устройстве, свежие сверху. */
function localGames(): Stored[] {
  const games: Stored[] = []

  for (const name of kv.keys()) {
    if (!name.startsWith(PREFIX)) continue

    const game = read(name.slice(PREFIX.length))

    if (game !== null) games.push(game)
  }

  return games.sort((one, other) => other.at - one.at)
}

/**
 * Начать партию за одним устройством.
 *
 * Лобби у неё нет: собирать некого — все уже здесь. Цвета садятся так же,
 * как их рассаживает сервер: вдвоём по разные стороны доски, вчетвером по
 * всем углам.
 */
export function createLocalGame(
  seats: number,
  bot: BotLevel | null = null,
  variant: Variant = 'classic',
): string {
  const players = Array.from(
    { length: seats },
    (_, seat) => ORDER[Math.floor((seat * ORDER.length) / seats)],
  )

  const game: Stored = {
    id: crypto.randomUUID(),
    at: Date.now(),
    players,
    // Первое место всегда за человеком: партию заводит он.
    bots: players.map((_, seat) => (seat === 0 ? null : bot)),
    tokens: startState(players).tokens as Record<string, TokenPosition[]>,
    turn: beginTurn(players[0]),
    status: 'playing',
    winner: null,
    feed: [],
    seq: 0,
    variant,
    // Круги считаются по ходу первого игрока — его и запоминаем.
    bonuses: variant === 'bonus' ? startField(players[0]) : null,
  }

  // Первый ход тоже начинается: у поля бонусов с него идёт счёт кругов.
  beginField(game, players[0])

  return keep(game, 'На одном устройстве')
}

/**
 * Начать обучение: партию с компьютером, расставленную заранее (tutorial.ts).
 *
 * За компьютера ходит сценарий, сеть ему не нужна. Уровень указан лишь
 * потому, что по нему экран отличает компьютер от человека.
 */
export function createTutorial(): string {
  const players = TUTORIAL_PLAYERS

  return keep(
    {
      id: crypto.randomUUID(),
      at: Date.now(),
      players,
      bots: players.map((_, seat) => (seat === 0 ? null : 'easy')),
      tokens: tutorialTokens(),
      turn: beginTurn(players[0]),
      status: 'playing',
      winner: null,
      feed: [],
      seq: 0,
      variant: 'classic',
      bonuses: null,
      tutorial: { at: 0, played: [] },
    },
    'Обучение',
  )
}

function keep(game: Stored, label: string): string {
  // Место под новую партию: старые вытесняются, иначе доигранные копились
  // бы в хранилище до конца времён.
  for (const old of localGames().slice(KEEP - 1)) {
    dropLocalGame(old.id)
  }

  write(game)
  // Партия попадает в общий список своих: кнопка «Продолжить» на главной
  // собирается из него, и партия за одним устройством ничем не хуже сетевой.
  // Секрет ей не нужен — спрашивать её состояние не у кого.
  rememberSecret(game.id, 'local', label)

  return game.id
}

/** Бросить кубики за того, чей сейчас ход. */
export function localRoll(id: string): GameView {
  const game = load(id)

  if (game.status !== 'playing' || !game.turn.awaiting) return view(game)

  const state = stateOf(game)
  const scripted = game.tutorial === undefined ? null : beatAt(game.tutorial)
  const turn = rollTurn(
    game.turn,
    state,
    scripted !== null && 'roll' in scripted ? scriptedRoller(scripted.roll) : randomRoller(),
  )

  if (game.tutorial !== undefined && scripted !== null && 'roll' in scripted) {
    game.tutorial = { at: game.tutorial.at + 1, played: [] }
  }

  game.turn = turn
  // Бросок в ленте нужен и здесь: по нему панель кубиков показывает, что
  // выпало, а очередь показа — что бросок уже случился.
  log(game, 'roll', {
    color: turn.player,
    dice: turn.rolls[turn.rolls.length - 1] ?? [],
    bonus: turn.rolls.length > 1,
  })

  if (turn.finished) {
    // Ходить нечем: кубики сгорели, ход уходит дальше. Без записи в ленте
    // это выглядит как исчезнувший ход.
    log(game, 'skip', { color: turn.player })
    pass(game)
  }

  write(game)

  return view(game)
}

/**
 * Сходить: один шаг или сразу два — ход «на сумму».
 *
 * Каждый шаг проверяется заново, уже по состоянию после предыдущего, — тем
 * же способом, что и на сервере: по тройке «фишка, кубик, вид хода».
 */
export function localMove(id: string, steps: MoveStep[]): GameView {
  const game = load(id)

  if (game.status !== 'playing' || steps.length === 0) return view(game)

  let state = stateOf(game)
  let turn = game.turn
  let won = false

  for (const step of steps) {
    const move = allowedFor(turn, state).find(
      (candidate) =>
        candidate.token === step.token &&
        candidate.die === step.die &&
        candidate.kind === step.kind,
    )

    if (move === undefined) break

    // Обучение пропускает только ход из сценария: экран других и не
    // предлагает, но проверка здесь, как и у сервера, а не в интерфейсе.
    if (game.tutorial !== undefined && !followScript(game, game.tutorial, step)) break

    state = applyMove(state, move)
    turn = playTurn(turn, move)

    log(game, 'move', move as unknown as Record<string, unknown>)
    ;[state, turn] = pickUp(game, move, state, turn)

    if (hasWon(state, turn.player)) {
      won = true
      break
    }
  }

  game.tokens = state.tokens as Record<string, TokenPosition[]>

  if (won) {
    game.status = 'finished'
    game.winner = turn.player
    game.turn = turn
    log(game, 'win', { color: turn.player })
    write(game)
    /*
     * Отметка «доиграна» ставится ЗДЕСЬ, а не только на экране партии.
     *
     * Экран ставит её, когда видит итог, — и этого хватает, пока итог
     * смотрят. Но партию за одним устройством никто не «покидает»: закрыл
     * вкладку на победном ходу — и она осталась бы в «Продолжить» навсегда.
     * Здесь же про окончание известно наверняка и без сети.
     */
    markOver(game.id)

    return view(game)
  }

  // settle только после последнего шага: между шагами кубики ещё не
  // кончились, и добросы начислять рано.
  game.turn = settleTurn(turn, state)

  if (game.turn.finished) pass(game)

  write(game)

  return view(game)
}

/**
 * Один шаг компьютера: бросить кубики или сходить.
 *
 * По одному действию за вызов, а не весь ход целиком: доска показывает ходы
 * по очереди, и сыпать их пачкой значит не дать ни один рассмотреть. Темп
 * задаёт тот, кто зовёт (useLocalBot).
 *
 * Сеть приходит снаружи: её веса грузятся отдельным чанком (bot/net/levels),
 * и ждать загрузки — забота того, кто зовёт, а не хранилища партии.
 */
export function localBotStep(id: string, net: Model): GameView {
  const game = load(id)

  if (game.status !== 'playing') return view(game)

  const level = game.bots[game.players.indexOf(game.turn.player)] ?? null

  if (level === null) return view(game)

  if (game.turn.awaiting) return localRoll(id)

  const scripted = game.tutorial === undefined ? null : beatAt(game.tutorial)

  if (scripted !== null && 'move' in scripted) return localMove(id, scripted.move)

  const moves = levelBrain(
    net,
    level,
    Math.random,
  )({ state: stateOf(game), turn: game.turn, field: game.bonuses ?? null })

  // Ходить нечем движок бы не позволил: ход, в котором кубики не играются,
  // закрывается сам в settle. Но замереть насмерть из-за этого нельзя.
  if (moves.length === 0) return view(game)

  return localMove(
    id,
    moves.map((move) => ({ token: move.token, die: move.die, kind: move.kind })),
  )
}

/** Состояние партии в том же виде, в каком его отдаёт сервер. */
export function localView(id: string): GameView {
  return view(load(id))
}

function load(id: string): Stored {
  const game = read(id)

  if (game === null) throw new Error('партия не найдена')

  return game
}

function stateOf(game: Stored): GameState {
  const state = restoreState(game.players, game.tokens)

  // Щиты — часть поля бонусов, но рубку запрещает движок: поэтому они едут
  // в расстановку здесь, как и на сервере (Game::gameState).
  return game.bonuses == null ? state : withShields(state, shieldedRefs(game.bonuses))
}

/** Передать ход следующему по кругу. Кубики не бросаются: это дело игрока. */
function pass(game: Stored): void {
  const at = game.players.indexOf(game.turn.player)
  const next = game.players[(at + 1) % game.players.length]

  game.turn = beginTurn(next)
  beginField(game, next)
}

/**
 * Начало хода на поле бонусов — как GameService::turnField: убавить щиты
 * ходящего, в начале круга убрать залежавшееся и выложить новый набор.
 */
function beginField(game: Stored, color: PlayerColor): void {
  if (game.bonuses == null) return

  const [field, events] = beginFieldTurn(game.bonuses, color, stateOf(game))

  game.bonuses = field

  for (const event of events) log(game, 'bonus', event)
}

/**
 * Фишка закончила шаг — забрать бонус с её клетки (GameService::pickUp).
 *
 * Сундук за одним устройством монет не приносит: по правилу заданий они
 * идут только за партии, которые считает сервер. Поэтому в нём здесь всегда
 * доброс или щит — пустым сундук не бывает.
 */
function pickUp(game: Stored, move: Move, state: GameState, turn: Turn): [GameState, Turn] {
  if (game.bonuses == null || move.to.location !== 'ring') return [state, turn]

  const [taken, kind] = takeBonus(game.bonuses, move.to.index)

  if (kind === null) return [state, turn]

  const prize = kind === 'chest' ? drawPrize(false) : null
  let field = taken
  let next = turn

  if (kind === 'roll' || prize === 'roll') next = withExtraRoll(turn)
  if (kind === 'shield' || prize === 'shield') {
    field = shieldToken(field, { color: move.color, index: move.token })
  }

  game.bonuses = field
  log(game, 'bonus', {
    action: 'take',
    kind,
    color: move.color,
    token: move.token,
    cell: move.to.index,
    ...(prize === null ? {} : { prize }),
  })

  return [withShields(state, shieldedRefs(field)), next]
}

/**
 * Шаг хода по сценарию обучения? Если да — отметить его сыгранным, а когда
 * сыгран весь ход, перейти к следующему шагу сценария.
 */
function followScript(game: Stored, progress: TutorialProgress, step: MoveStep): boolean {
  const beat = beatAt(progress)

  // Сценарий кончился — партия обычная.
  if (beat === null) return true

  const at = expectedStep(beat, progress, step)

  if (at === null || !('move' in beat)) return false

  const played = [...progress.played, at]

  game.tutorial =
    played.length === beat.move.length
      ? { at: progress.at + 1, played: [] }
      : { ...progress, played }

  return true
}

function log(game: Stored, type: FeedEvent['type'], payload: Record<string, unknown>): void {
  game.seq += 1
  game.feed = [...game.feed, { seq: game.seq, type, payload }].slice(-FEED_LIMIT)
}

/** Как игрок подписан за столом. */
function nameOf(game: Stored, seat: number): string {
  const level = game.bots[seat] ?? null

  return level === null ? NAMES[game.players[seat]] : BOT_NAMES[seat % BOT_NAMES.length]
}

/**
 * Чьими глазами смотрит экран.
 *
 * Ходит человек — его же; ходит компьютер — всё равно человека, иначе экран
 * предложил бы бросить кубики за соперника. Партия кончилась — победителя,
 * если победил человек: за одним устройством он тут же, рядом, и «вы
 * победили» написано ровно про него.
 */
function seatOfViewer(game: Stored, current: PlayerColor | null): GameView['you'] {
  const acting =
    current === null
      ? game.players.indexOf(game.winner ?? game.turn.player)
      : game.players.indexOf(current)
  const human = acting >= 0 && game.bots[acting] === null ? acting : game.bots.indexOf(null)

  if (human < 0) return null

  return {
    color: game.players[human],
    name: nameOf(game, human),
    seat: human,
    host: true,
  }
}

function view(game: Stored): GameView {
  const state = stateOf(game)
  const playing = game.status === 'playing'
  const current = playing ? game.turn.player : null
  const beat = game.tutorial === undefined ? null : beatAt(game.tutorial)
  const progress = game.tutorial ?? { at: 0, played: [] }
  const allowed = narrowMoves(
    beat,
    progress,
    playing && !game.turn.finished ? allowedFor(game.turn, state) : [],
  )
  const pairs = narrowCombos(
    beat,
    progress,
    (playing && !game.turn.finished
      ? combos(state, game.turn.player, [...game.turn.dice])
      : []
    ).map(comboView),
  )

  return {
    id: game.id,
    status: game.status,
    visibility: 'private',
    variant: game.variant ?? 'classic',
    bonuses:
      game.bonuses == null
        ? null
        : {
            items: game.bonuses.items.map((item) => ({
              cell: item.index,
              kind: item.kind,
              expiring: isExpiring(game.bonuses as BonusField, item),
            })),
            shields: game.bonuses.shields.map((shield) => ({ ...shield, left: shield.left ?? 1 })),
          },
    currentColor: current,
    winnerColor: game.winner,
    players: game.players.map((color, seat) => ({
      color,
      name: nameOf(game, seat),
      seat,
      startRoll: null,
      bot: game.bots[seat] ?? null,
      userId: null,
      avatar: null,
      ready: true,
    })),
    tokens: game.tokens,
    turn: playing ? turnView(game.turn) : null,
    allowed,
    combos: pairs,
    feed: game.feed,
    /*
     * «Вы» — тот, чей сейчас ход: телефон переходит из рук в руки, и играет
     * всегда тот, кто его держит. Иначе экран считал бы игрока зрителем и
     * не давал бы ни бросить кубики, ни выбрать ход.
     */
    /*
     * «Вы» — тот, чей сейчас ход, если это человек. Пока ходит компьютер,
     * им остаётся хозяин партии: иначе экран считал бы его игрока
     * компьютером и предложил бы бросить кубики за него.
     */
    you: seatOfViewer(game, current),
    local: true,
    tutorial:
      game.tutorial === undefined
        ? null
        : {
            text: sayOf(beat, progress),
            step: Math.min(game.tutorial.at + 1, TUTORIAL.length),
            total: TUTORIAL.length,
            done: beat === null,
            roll: beat !== null && 'roll' in beat ? (beat.tip ?? null) : null,
            pointers: pointersOf(beat, progress),
          },
  }
}
